import React from 'react'

import { Button } from '@clientbase/clientbase-library'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import { isRouteAllowedInGroup } from 'lib/merchant-group'
import { useRouter } from 'next/router'

import MerchantBadge from 'components/MerchantBadge'

import { theme } from 'styles/theme'

type GroupRouteGuardProps = {
  children: JSX.Element
}

/**
 * SC-10999: na visão multi-empresas só as telas agregadas funcionam (Visão
 * Geral, Cobranças e Recorrências, Contratos e a criação de contrato). O
 * resto é por empresa: em vez da tela, mostra o atalho para entrar numa das
 * empresas do grupo. Fora da visão do grupo é transparente.
 */
const GroupRouteGuard = ({ children }: GroupRouteGuardProps) => {
  const { pathname, query } = useRouter()
  const { groupSession, switchAccount, switchingTo } = useAuthContext()

  if (!groupSession) return children

  if (isRouteAllowedInGroup(pathname, query?.tab as string | undefined)) {
    return children
  }

  return (
    <Box p="20px">
      <Stack
        gap="16px"
        alignItems="center"
        justifyContent="center"
        width="100%"
        padding="50px 20px"
        borderRadius="10px"
        border={`1px solid ${theme.palette.neutral[700]}`}
        bgcolor="#fff"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="48px"
          height="48px"
          borderRadius="8px"
          bgcolor={theme.palette.neutral[800]}
          color={theme.palette.neutral[300]}
        >
          <GridViewRoundedIcon />
        </Box>

        <Stack gap="4px" alignItems="center">
          <Typography variant="mdBold" textAlign="center">
            Esta área é por empresa.
          </Typography>
          <Typography
            variant="smLight"
            textAlign="center"
            color={theme.palette.neutral[400]}
            maxWidth="28rem"
          >
            Escolha uma empresa para continuar. Você está vendo todas as
            empresas de {groupSession.name}.
          </Typography>
        </Stack>

        <Stack gap="8px" width="100%" maxWidth="360px">
          {groupSession.merchants.map((merchant) => (
            <Button
              key={merchant.merchant_uuid}
              variantButton="outlined"
              fullWidth
              disabled={!!switchingTo}
              onClick={() =>
                switchAccount({ merchantUuid: merchant.merchant_uuid })
              }
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                gap={1}
              >
                <MerchantBadge
                  name={merchant.name}
                  logoUrl={merchant.logo_url}
                  size={24}
                />
                {switchingTo === merchant.merchant_uuid && (
                  <CircularProgress size={16} />
                )}
              </Stack>
            </Button>
          ))}
        </Stack>
      </Stack>
    </Box>
  )
}

export default GroupRouteGuard
